/**
 * Chat Image Upload Service
 *
 * Handles compression and upload of chat images to Supabase Storage.
 * Uses canvas.toBlob() directly (not base64 round-trip like PhotoSelectionOverlay).
 */

import { nanoid } from 'nanoid';
import { supabase } from '../supabase';
import { drawScaledCanvas } from '../../utils/imageScale';

/**
 * Compress an image file using Canvas API.
 * Progressive re-compression if output exceeds 500KB.
 *
 * @param {File} file - Image file from file input
 * @param {number} maxDimension - Max width/height in pixels
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<Blob>} Compressed JPEG blob
 */
export function compressImage(file, maxDimension = 1200, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      // Image is decoded — release the object URL or the full-size original
      // (multi-MB camera file) stays pinned in memory for the session
      URL.revokeObjectURL(img.src);
      try {
        // Pass 1: 1200px / 80% quality
        let blob = await compressToBlob(img, maxDimension, quality);
        console.log('[CHAT_IMAGE] Pass 1:', (blob.size / 1024).toFixed(0), 'KB');

        // Pass 2: if >500KB, try 1200px / 60% quality
        if (blob.size > 500 * 1024) {
          blob = await compressToBlob(img, maxDimension, 0.6);
          console.log('[CHAT_IMAGE] Pass 2:', (blob.size / 1024).toFixed(0), 'KB');
        }

        // Pass 3: if still >500KB, reduce to 800px / 60% quality
        if (blob.size > 500 * 1024) {
          blob = await compressToBlob(img, 800, 0.6);
          console.log('[CHAT_IMAGE] Pass 3:', (blob.size / 1024).toFixed(0), 'KB');
        }

        // If still >500KB after all passes: upload anyway — never hard-reject
        resolve(blob);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Compress image to a Blob at specified dimensions and quality.
 * Scaling lives in the shared drawScaledCanvas (downscale-only here).
 */
function compressToBlob(img, maxDimension, quality) {
  return new Promise((resolve, reject) => {
    const canvas = drawScaledCanvas(img, maxDimension);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('canvas.toBlob returned null'));
        }
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Compress and upload an image to a Supabase Storage bucket under
 * `${pathPrefix}/${timestamp}_${nanoid}.jpg`. Shared by chat images and
 * post photos — one upload flow, two buckets.
 *
 * @param {string} bucket - Storage bucket id
 * @param {string} pathPrefix - Path prefix (chatRoomId, profileUserId, …)
 * @param {File} file - Image file from file input
 * @param {string} [logTag] - Console tag for upload errors
 * @returns {Promise<string>} Public URL of uploaded image
 * @throws {Error} If compression or upload fails
 */
export async function uploadCompressedImage(bucket, pathPrefix, file, logTag = '[IMAGE]') {
  const blob = await compressImage(file);

  const path = `${pathPrefix}/${Date.now()}_${nanoid(8)}.jpg`;

  const { error } = await supabase.storage.from(bucket).upload(path, blob, {
    contentType: 'image/jpeg',
  });

  if (error) {
    console.error(`${logTag} Upload failed:`, error);
    throw error;
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from(bucket).getPublicUrl(path);

  return publicUrl;
}

/**
 * Compress and upload a chat image to Supabase Storage.
 *
 * @param {File} file - Image file from file input
 * @param {string} chatRoomId - Chat room identifier for path organization
 * @returns {Promise<string>} Public URL of uploaded image
 * @throws {Error} If upload fails
 */
export async function uploadChatImage(file, chatRoomId) {
  const publicUrl = await uploadCompressedImage('chat-images', chatRoomId, file, '[CHAT_IMAGE]');
  console.log('[CHAT_IMAGE] Uploaded:', publicUrl);
  return publicUrl;
}
